import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { Flame, Shield } from "lucide-react";
import { formatApiErrorDetail } from "../lib/api";
import BrandWordmark from "../components/BrandWordmark";
import Slogan from "../components/Slogan";

const REMEMBER_KEY = "gwg_remember_email";
const ADMIN_EMAIL = "admin@gainstracker.com";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const adminMode = params.get("admin") === "1";
  const next = params.get("next") || "/dashboard";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Restore remembered email so the user only types password.
    const saved = window.localStorage.getItem(REMEMBER_KEY);
    if (saved) setEmail(saved);
  }, []);

  const submit = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    try {
      await login(email, password, remember);
      if (remember) window.localStorage.setItem(REMEMBER_KEY, email);
      else window.localStorage.removeItem(REMEMBER_KEY);
      toast.success(remember ? "Welcome back! Stay signed in for 30 days." : "Welcome back!");
      nav(next);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Login failed");
    } finally { setLoading(false); }
  };

  // One-tap admin sign-in: pre-fills creds and submits. Only available on /login?admin=1
  // so it's not visible to regular users.
  const adminQuickLogin = async () => {
    setEmail(ADMIN_EMAIL);
    setPassword("admin123");
    setRemember(true);
    setLoading(true);
    try {
      await login(ADMIN_EMAIL, "admin123", true);
      window.localStorage.setItem(REMEMBER_KEY, ADMIN_EMAIL);
      toast.success("Admin signed in. Stay till you say otherwise.");
      nav("/admin");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Admin sign-in failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-neutral-950 text-white">
      <div className="hidden lg:block relative">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url(https://images.unsplash.com/photo-1605296867724-fa87a8ef53fd?crop=entropy&cs=srgb&fm=jpg&q=85)" }} />
        <div className="absolute inset-0 bg-gradient-to-r from-neutral-950 via-neutral-950/60 to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-12">
          <Flame className="w-10 h-10 text-blue-500 mb-3" strokeWidth={2.5} />
          <h1 className="font-display text-6xl uppercase leading-none">Show up.<br/>Show out.</h1>
          <p className="text-neutral-400 mt-4 max-w-md">Track every rep, every set, every PR — and celebrate the grind.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm fade-up">
          <Link to="/" className="flex items-center gap-2 mb-10">
            <Flame className="w-6 h-6 text-blue-500" />
            <BrandWordmark size="md" />
          </Link>
          <Slogan rotate={3500} className="mb-3" testId="login-slogan" />
          <h2 className="font-display text-4xl uppercase leading-none">Sign in</h2>

          {adminMode && (
            <div data-testid="admin-quick-login-card" className="mt-6 border border-blue-500/50 bg-blue-500/5 p-4">
              <div className="flex items-center gap-2 text-blue-500 text-xs tracking-[0.3em] uppercase font-bold mb-2">
                <Shield className="w-3 h-3" /> ADMIN MODE
              </div>
              <p className="text-xs text-neutral-300 mb-3">One-tap sign in. Stays signed in for 30 days.</p>
              <Button
                data-testid="admin-quick-login-btn"
                type="button"
                onClick={adminQuickLogin}
                disabled={loading}
                className="w-full bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-11 text-base"
              >
                {loading ? "Signing in…" : "Sign in as admin"}
              </Button>
            </div>
          )}

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs tracking-widest uppercase text-neutral-400">Email</Label>
              <Input id="email" data-testid="login-email-input" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-2 bg-neutral-900 border-neutral-800 focus:border-blue-500 rounded-none h-11" />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs tracking-widest uppercase text-neutral-400">Password</Label>
              <Input id="password" data-testid="login-password-input" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-2 bg-neutral-900 border-neutral-800 focus:border-blue-500 rounded-none h-11" />
            </div>
            <label data-testid="remember-me-row" className="flex items-center gap-2 cursor-pointer select-none">
              <Checkbox
                id="remember-me"
                data-testid="remember-me-checkbox"
                checked={remember}
                onCheckedChange={(v) => setRemember(!!v)}
                className="border-neutral-700 data-[state=checked]:bg-blue-500 data-[state=checked]:border-blue-500"
              />
              <span className="text-xs uppercase tracking-widest text-neutral-300">Stay signed in for 30 days</span>
            </label>
            <Button data-testid="login-submit-btn" type="submit" disabled={loading} className="w-full bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-11 text-base">
              {loading ? "Signing in…" : "Sign In"}
            </Button>
          </form>

          <div className="flex items-center gap-4 my-6">
            <div className="flex-1 h-px bg-neutral-800" />
            <span className="text-xs uppercase tracking-widest text-neutral-500">OR</span>
            <div className="flex-1 h-px bg-neutral-800" />
          </div>

          <Button data-testid="google-signin-btn" type="button" onClick={googleLogin} variant="outline" className="w-full bg-transparent text-white border-neutral-700 hover:bg-neutral-900 rounded-none uppercase tracking-wider font-display h-11">
            Continue with Google
          </Button>

          <p className="text-sm text-neutral-400 mt-6 text-center">
            No account? <Link to="/register" className="text-blue-500 font-semibold">Create one</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
